import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Router from './Router';
import { GlobalStyle } from './styles';
import { YMInitializer } from 'react-yandex-metrika';
import GlobalLoader from '../components/GlobalLoader.jsx';
import DevTools from '../components/DevTools.jsx';
import VersionPanel from '../components/VersionPanel.jsx';
import AppErrorBoundary from '../components/AppErrorBoundary.jsx';
import AdvancedFallback from '../components/AdvancedFallback.jsx';
import styled from 'styled-components';
import useDeviceInfo from '../hooks/useDeviceInfo.js';
import { useCronHealthCheck } from '../hooks/useCronHealthCheck.js';
import useErrorTracking from '../hooks/useErrorTracking.js';
import useDevMode from '../hooks/useDevMode.js';
import useGlobalLoader from '../hooks/useGlobalLoader.js';
import useAppNavigation from '../hooks/useAppNavigation.js';
import useAuth from '../hooks/useAuth.js';
import useKitchenTabSync from '../hooks/useKitchenTabSync.js';
import useTimeSync from '../hooks/useTimeSync.js';
import useCurrentServerSync from '../hooks/useCurrentServerSync.js';
import NetworkStatusLamp from '../components/NetworkStatusLamp.jsx';
import { subscribe as subscribeNetwork, getState as getNetworkState } from '../components/NetworkStatusLamp.jsx';
import OfflineFallback from '../components/OfflineFallback.jsx';
import { setAuth } from '../redux/reducers/utils/authReducer.js';

const App = () => {
  const location = useLocation();
  const dispatch = useDispatch();

  //Временное решение для очистки localStorage на старом домене
  const isRestarted = localStorage.getItem('restarted') || false;
  console.log('App mounted. Restarted:', isRestarted);
  if (!isRestarted) {
    try {
      dispatch(setAuth(false));

      try {
        // Удаляем только чувствительные ключи, чтобы не потерять идентификатор устройства
        const keysToRemove = ['token', 'session'];
        keysToRemove.forEach((k) => {
          try { localStorage.removeItem(k); } catch (e) { /* noop */ }
        });
      } catch (e) {
        console.error('localStorage cleanup failed', e);
      }
      
      try {
        // Отмечаем, что рестарт выполнен, чтобы не повторять очистку при следующем монтировании
        localStorage.setItem('restarted', '1');
      } catch (e) { /* noop */ }
      window.location.reload();
    } catch (error) {
      window.location.reload();
    }
  };

  useTimeSync();
  const [networkState, setNetworkState] = useState(() => getNetworkState());

  useEffect(() => {
    const unsubscribe = subscribeNetwork((next) => setNetworkState(next));
    return () => unsubscribe();
  }, []);
  // Селекторы состояния
  const { isAuth, globalLoader, isDevMode } = useSelector((store) => ({
    isAuth: store.auth.isAuth,
    globalLoader: store.common.globalLoader,
    isDevMode: store.common.isDevMode,
  }));
  

  // Инициализация всех хуков
  useDeviceInfo();
  const notificationHolder = useCronHealthCheck();
  useErrorTracking();
  useDevMode();
  useGlobalLoader();
  useAppNavigation();
  useKitchenTabSync();
  useCurrentServerSync();
  useAuth();

  const actuallyOnline = networkState?.isOnline && networkState?.isReallyOnline;

  if (!actuallyOnline) {
    return (
      <>
        <GlobalStyle />
        <OfflineFallback />
        {/* Информационная панель */}
        <AppInfo>
          <NetworkStatusLamp />
          <VersionPanel />
        </AppInfo>
        {/* Dev tools доступен даже при офлайне */}
        <DevTools />
      </>
    );
  }

  return (
    <>
      {isDevMode && notificationHolder}
      <GlobalStyle />
      <YMInitializer
          accounts={[106283817]}
          options={{
              clickmap: true,
              trackLinks: true,
              accurateTrackBounce: true,
              webvisor: true,
          }}
          version="2"
      />

      {/* Глобальный лоадер показываем только авторизованным пользователям */}
      {isAuth && globalLoader && location.pathname !== '/services/roleaccess' && (
        <GlobalLoader />
      )}
      
      <AppErrorBoundary fallback={<AdvancedFallback />}>
        <Router />
      </AppErrorBoundary>

      {/* Информационная панель */}
      <AppInfo>
        <NetworkStatusLamp />
        <VersionPanel />
      </AppInfo>
      
      {/* Инструменты разработки */}
      <DevTools />
    </>
  );
};

const AppInfo = styled.div`
  position: fixed;
  bottom: 0;
  left: 10px;
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 10px;
  @media print {
    display: none;
  }
`;

export default App;
